// shared-components/core/services/EmailDeliveryService.js

import { getSupabaseClient } from "./SupabaseService";

/**
 * Email Delivery Service
 *
 * Manages email delivery tracking and monitoring for the multi-tenant platform.
 * Provides admin dashboard functionality for viewing email delivery stats,
 * logs, and manual resend capabilities.
 *
 * Features:
 * - Fetch delivery statistics with success/failure rates
 * - Query email logs with advanced filtering
 * - Manual email resend for failed deliveries
 * - Mark emails as resolved
 *
 * @class EmailDeliveryService
 */
class EmailDeliveryService {
  constructor() {
    this.supabase = getSupabaseClient();
  }

  /**
   * Get email delivery statistics using database function
   *
   * @param {string} websiteId - Website UUID
   * @param {number} days - Number of days to analyze (default: 7)
   * @returns {Promise<Object>} Statistics object with success rates, counts, and trends
   */
  async getEmailDeliveryStats(websiteId, days = 7) {
    try {
      const { data, error } = await this.supabase.rpc(
        "get_email_delivery_stats",
        {
          p_website_id: websiteId,
          p_days: days,
        },
      );

      if (error) throw error;

      // Parse JSON response from database function
      return (
        data || {
          total_emails: 0,
          successful: 0,
          failed: 0,
          pending: 0,
          success_rate_pct: 0,
          avg_retry_count: 0,
          max_retry_count: 0,
          emails_needing_retry: 0,
          by_type: {},
          daily_trend: [],
        }
      );
    } catch (error) {
      console.error("Error fetching email delivery stats:", error);
      throw new Error(`Failed to fetch email statistics: ${error.message}`);
    }
  }

  /**
   * Fetch email delivery logs with filters
   *
   * @param {string} websiteId - Website UUID
   * @param {Object} filters - Filter options
   * @param {string} filters.status - Email status filter (sent, failed, bounced, pending_retry)
   * @param {string} filters.emailType - Email type filter (order_confirmation, download_link, etc.)
   * @param {string} filters.dateFrom - ISO date string for start date
   * @param {string} filters.dateTo - ISO date string for end date
   * @param {string} filters.orderReference - Filter by order reference
   * @param {string} filters.searchTerm - Search in subject or error message
   * @param {number} filters.limit - Results per page (default: 20)
   * @param {number} filters.offset - Pagination offset (default: 0)
   * @returns {Promise<Object>} Object with data array and count
   */
  async getEmailDeliveryLogs(websiteId, filters = {}) {
    try {
      const {
        status,
        emailType,
        dateFrom,
        dateTo,
        orderReference,
        searchTerm,
        limit = 20,
        offset = 0,
      } = filters;

      // Build query
      let query = this.supabase
        .from("email_delivery_logs")
        .select("*, orders(order_reference)", { count: "exact" })
        .eq("website_id", websiteId)
        .order("created_at", { ascending: false });

      // Apply filters
      if (status && status !== "all") {
        query = query.eq("status", status);
      }

      if (emailType && emailType !== "all") {
        query = query.eq("email_type", emailType);
      }

      if (dateFrom) {
        query = query.gte("created_at", dateFrom);
      }

      if (dateTo) {
        query = query.lte("created_at", dateTo);
      }

      if (orderReference) {
        // Join with orders table to filter by reference
        query = query.not("order_id", "is", null);
      }

      if (searchTerm) {
        query = query.or(
          `subject.ilike.%${searchTerm}%,error_message.ilike.%${searchTerm}%`,
        );
      }

      // Apply pagination
      query = query.range(offset, offset + limit - 1);

      const { data, error, count } = await query;

      if (error) throw error;

      // Filter by order reference if provided (post-query since it's in joined table)
      let filteredData = data || [];
      if (orderReference) {
        filteredData = filteredData.filter((log) =>
          log.orders?.order_reference
            ?.toLowerCase()
            .includes(orderReference.toLowerCase()),
        );
      }

      return {
        data: filteredData,
        count: count || 0,
      };
    } catch (error) {
      console.error("Error fetching email delivery logs:", error);
      throw new Error(`Failed to fetch email logs: ${error.message}`);
    }
  }

  /**
   * Get single email log details
   *
   * @param {string} logId - Email log UUID
   * @returns {Promise<Object>} Email log with full details
   */
  async getEmailLogDetails(logId) {
    try {
      const { data, error } = await this.supabase
        .from("email_delivery_logs")
        .select("*, orders(order_reference, customer_email)")
        .eq("id", logId)
        .single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error("Error fetching email log details:", error);
      throw new Error(`Failed to fetch email details: ${error.message}`);
    }
  }

  /**
   * Manual email resend
   *
   * This method triggers a manual resend by calling the appropriate Edge Function
   * based on the email type. Currently supports order confirmation emails.
   *
   * @param {string} logId - Email log UUID
   * @param {string} websiteId - Website UUID
   * @returns {Promise<Object>} Resend result
   */
  async resendEmail(logId, _websiteId) {
    try {
      // First, get the email log details to determine email type
      const logDetails = await this.getEmailLogDetails(logId);

      if (!logDetails) {
        throw new Error("Email log not found");
      }

      // Check if max retries reached
      if (logDetails.retry_count >= logDetails.max_retries) {
        throw new Error(
          `Maximum retry attempts (${logDetails.max_retries}) reached for this email`,
        );
      }

      // For now, this is a placeholder - actual implementation would call
      // the appropriate Edge Function based on email_type
      // Example: stripe-webhook for order_confirmation emails

      // For order confirmation emails, we need to trigger the webhook again
      if (
        logDetails.email_type === "order_confirmation" &&
        logDetails.order_id
      ) {
        // This would typically call a dedicated resend endpoint
        // For now, we'll update the log to trigger retry via the background job
        const { error } = await this.supabase.rpc("mark_email_retry", {
          p_log_id: logId,
          p_error_code: null,
          p_error_message: "Manual resend requested by admin",
        });

        if (error) throw error;

        return {
          success: true,
          message: "Email queued for resend",
          scheduled_at: new Date(),
        };
      }

      throw new Error(
        `Email type '${logDetails.email_type}' resend not implemented`,
      );
    } catch (error) {
      console.error("Error resending email:", error);
      throw new Error(`Failed to resend email: ${error.message}`);
    }
  }

  /**
   * Mark email as resolved (admin acknowledgment)
   *
   * @param {string} logId - Email log UUID
   * @param {string} resolutionNote - Admin note about resolution
   * @returns {Promise<Object>} Updated log
   */
  async markEmailResolved(logId, resolutionNote) {
    try {
      // Add resolution note to error_message field (prefixed)
      const { data, error } = await this.supabase
        .from("email_delivery_logs")
        .update({
          error_message: `[RESOLVED by admin] ${resolutionNote}`,
          updated_at: new Date().toISOString(),
        })
        .eq("id", logId)
        .select()
        .single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error("Error marking email as resolved:", error);
      throw new Error(`Failed to mark email as resolved: ${error.message}`);
    }
  }

  /**
   * Get failed emails ready for retry
   *
   * @param {string} websiteId - Website UUID
   * @param {number} hours - Look back hours (default: 24)
   * @returns {Promise<Array>} List of failed emails ready for retry
   */
  async getFailedEmailsForRetry(websiteId, hours = 24) {
    try {
      const { data, error } = await this.supabase.rpc(
        "get_failed_emails_for_website",
        {
          p_website_id: websiteId,
          p_hours: hours,
        },
      );

      if (error) throw error;

      return data || [];
    } catch (error) {
      console.error("Error fetching failed emails for retry:", error);
      throw new Error(`Failed to fetch retry queue: ${error.message}`);
    }
  }
}

// Export singleton instance
export const emailDeliveryService = new EmailDeliveryService();
